import { DEFAULTAVATAR, getAllProfessionalApi } from 'Constants';
import { Context } from 'Context/Context';
import CompleteUserDetails from 'Modals/CompleteUserDetails';
import moment from 'moment';
import React, { useContext, useEffect, useState } from 'react'
import { toast } from 'react-toastify';
import TimeframeHeader from './TimeframeHeader';
import { C, DataTable, Pagination, ExportMenu, FilterRow, ResultCount, downloadRows, cell } from './AdminAnalyticsUI';

const PAGE_SIZE = 20;

const getClosedCases = (label, data, oldCases) => {
  let count = 0;
  data?.forEach((d) => { if (d.caseStatus === label) count++; });
  oldCases?.forEach((d) => d?.forEach((inner) => { if (inner.caseStatus === label) count++; }));
  return count;
};
const getRequestedCases = (cases, oldCases) => {
  let count = 0;
  oldCases?.forEach((item) => { count += parseInt(item.length) || 0; });
  return (parseInt(cases?.length) || 0) + count;
};
const fullName = (u) => [u.firstName, u.lastName].filter(Boolean).join(' ');

function ProfessionalUserAnalytics() {
    const {makeApiCallFunc} = useContext(Context);
    const [profData, setProfData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [loadList, setLoadList] = useState(false);
    const [dateRange, setDateRange] = useState([{ startDate: '', endDate: '', key: 'selection' }]);
    const [frame, setFrame] = useState("all");
    const [userData, setUserData] = useState(null);
    const [totalCount, setTotalCount] = useState(0);
    const [skip, setSkip] = useState(0);
    const [exporting, setExporting] = useState(false);

    const dateFilter = () => {
      const startTime = dateRange[0]?.startDate ? dateRange[0].startDate : null;
      const endTime = dateRange[0]?.endDate ? dateRange[0].endDate : null;
      return startTime === null ? {} : { startTime, endTime };
    };

    useEffect(() => {
      window.scrollTo(0,0);
      setLoading(true);
      makeApiCallFunc(getAllProfessionalApi,
        { role: 2, ...dateFilter(), skip, limit: PAGE_SIZE },
        ({data})=> {
          setProfData(data?.modifiedResponse || []);
          setTotalCount(data?.totalCount || 0);
          setLoading(false);
        },
        (data) => { console.log(data); setLoading(false); }
      )
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },[loadList, skip])

    const rows = (profData || []).map((r, i) => ({ ...r, _idx: skip + i + 1 }));
    const page = Math.floor(skip / PAGE_SIZE) + 1;
    const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

    const columns = [
      { label: '#', width: '44px', sortVal: (r) => r._idx, render: (r) => <span style={cell.index}>{r._idx}</span> },
      { label: 'Name', width: 'minmax(0,1.4fr)', sortVal: (r) => fullName(r).toLowerCase(), render: (r) => (
        <div className='flex items-center min-w-0' style={{ gap: '10px' }}>
          <div className='relative flex-none'>
            <img alt="" src={r.profilePic ? r.profilePic : DEFAULTAVATAR} className='rounded-full object-cover' style={{ width: '28px', height: '28px', border: r.verified ? '1.5px solid ' + C.green : '1px solid rgba(20,33,61,.12)' }} />
          </div>
          <div className='min-w-0'>
            <div className='truncate' style={cell.primary}>{fullName(r) || '—'}</div>
            {r.verified && <div style={{ fontSize: '11px', fontWeight: 600, color: C.green }}>Verified</div>}
          </div>
        </div>
      ) },
      { label: 'Email', width: 'minmax(0,1.5fr)', sortVal: (r) => (r.email || '').toLowerCase(), render: (r) => <div className='truncate' style={cell.muted}>{r.email || '—'}</div> },
      { label: 'Legal type', width: 'minmax(0,1fr)', sortVal: (r) => (r.legalType || '').toLowerCase(), render: (r) => <div className='truncate' style={r.legalType ? cell.muted : cell.empty}>{r.legalType || '—'}</div> },
      { label: 'Closed', width: '70px', right: true, sortVal: (r) => getClosedCases('closed', r.caseDetails, r.oldCases), render: (r) => { const v = getClosedCases('closed', r.caseDetails, r.oldCases); return <span style={cell.num(v)}>{v}</span>; } },
      { label: 'Requests', width: '80px', right: true, sortVal: (r) => getRequestedCases(r.caseDetails, r.oldCases), render: (r) => { const v = getRequestedCases(r.caseDetails, r.oldCases); return <span style={cell.num(v)}>{v}</span>; } },
      { label: 'Phone', width: '120px', sortVal: (r) => r.phoneNumber || '', render: (r) => <span style={r.phoneNumber ? cell.muted : cell.empty}>{r.phoneNumber || '—'}</span> },
      { label: 'Logins', width: '70px', right: true, sortVal: (r) => parseInt(r.totalLoginCount) || 0, render: (r) => { const v = parseInt(r.totalLoginCount) || 0; return <span style={cell.num(v)}>{v}</span>; } },
      { label: 'Joined on', width: '96px', sortVal: (r) => new Date(r.timestamp || 0).getTime(), render: (r) => <span style={r.timestamp ? cell.date : cell.empty}>{r.timestamp ? moment(r.timestamp).format('MM/DD/YYYY') : '—'}</span> },
    ];

    const doExport = (format) => {
      setExporting(true);
      makeApiCallFunc(getAllProfessionalApi,
        { role: 2, ...dateFilter(), skip: 0, limit: Math.max(totalCount, 1) },
        async ({data}) => {
          try {
            const all = data?.modifiedResponse || [];
            const out = [
              ['#', 'Name', 'Email', 'Legal type', 'Verified', 'Closed cases', 'Total requests', 'Phone', 'Successful logins', 'Joined on'],
              ...all.map((r, i) => [i + 1, fullName(r), r.email || '', r.legalType || '', r.verified ? 'Yes' : 'No',
                getClosedCases('closed', r.caseDetails, r.oldCases), getRequestedCases(r.caseDetails, r.oldCases),
                r.phoneNumber || '', parseInt(r.totalLoginCount) || 0, r.timestamp ? moment(r.timestamp).format('MM/DD/YYYY') : '']),
            ];
            await downloadRows(out, format, 'QCR_professionals' + (frame !== 'all' ? '_' + frame : '') + '_' + moment().format('YYYY-MM-DD'));
          } catch (e) { console.log(e); toast.error('Export failed. Please try again.'); }
          setExporting(false);
        },
        (e) => { console.log(e); toast.error('Export failed. Please try again.'); setExporting(false); }
      );
    };

  return (
    <div className='w-full font-plexsans'>
        {
          showModal &&
          <CompleteUserDetails uploadNew={showModal} onClose={()=>setShowModal(false)} user = {userData} />
        }
        <FilterRow
          label="Joined on"
          control={
            <TimeframeHeader
              className='flex items-center'
              showAll={true}
              setPaginateLimit={() => setSkip(0)}
              setLoadCaseAnalytics={setLoadList} frame={frame} setFrame={setFrame} setDateRange={setDateRange} dateRange={dateRange}/>
          }
          right={<ExportMenu onExport={doExport} exporting={exporting} />}
        />
        <ResultCount from={totalCount === 0 ? 0 : skip + 1} to={Math.min(skip + PAGE_SIZE, totalCount)} total={totalCount} noun="professionals" />
        <DataTable
          key={skip + '-' + frame}
          columns={columns}
          rows={rows}
          loading={loading}
          minWidth={960}
          emptyText="No professionals match these filters"
          onRowClick={(r) => { setUserData(r); setShowModal(true); }}
          footer={!loading && totalCount > 0 && <Pagination page={page} totalPages={totalPages} onPage={(p) => { if (p >= 1 && p <= totalPages) setSkip((p - 1) * PAGE_SIZE); }} />}
        />
    </div>
  )
}

export default ProfessionalUserAnalytics
